'use client';
import React, { useState, useEffect } from 'react';
import { PaymentMethod as Shared } from '@launchstore/shared-puck';
import { usePuckContext } from '@/components/puck/PuckContextProvider';
import { CART_UPDATED_EVENT } from '@/lib/hooks/useCart';
import { checkoutSelections, stepStatus } from './checkout-state';

const SharedRender = (Shared as any).render;

// Module-level state: persists the staging payment provider AND the
// last successfully-saved id across re-mounts of the wrapper. The
// Puck context is SSR-frozen, so we need `lastSavedId` to know what
// the cart has been updated to — without it, `cart.payment_collection`
// is empty for a window after the save and the "Review Order" button
// stays visible.
const paymentState: { stagingId: string; isSaving: boolean; lastSavedId: string } = {
  stagingId: '',
  isSaving: false,
  lastSavedId: '',
};

/**
 * Storefront PaymentMethod — reads available payment providers from
 * Puck context (injected by the checkout page from listCartPaymentMethods)
 * and passes just `{id}` per provider. The shared component looks up
 * friendly names + icons + descriptions from `PAYMENT_INFO_MAP`.
 *
 * On click: sets the staging `selectedId` (immediate visual feedback),
 * then POSTs to /api/cart/payment-method so Medusa initiates a payment
 * session for the chosen provider. Dispatches CART_UPDATED_EVENT so the
 * cart in Puck context (and any other useCart() instances) refetches
 * with the new payment session. No Stripe Elements — the actual
 * Apple Pay / Google Pay / PayPal UX is handled by Stripe / Medusa at
 * the time of payment, not in our UI.
 */
function PaymentMethodImpl(props: any) {
  const { context } = usePuckContext() as any;
  const cart = context?.cart;
  const providers: any[] | undefined = context?.availablePaymentMethods;

  // === All hooks must be called in the same order on every render. ===

  // Re-render trigger: Puck context is SSR-frozen, so without
  // listening to CART_UPDATED_EVENT the wrapper never re-evaluates
  // the step-gating after ShippingMethod saves its selection. Bumping
  // `trigger` forces a fresh render so the gate sees the new
  // `checkoutSelections.shippingId`.
  const [trigger, setTrigger] = useState(0);
  useEffect(() => {
    const onCartUpdated = () => setTrigger((t) => t + 1);
    window.addEventListener(CART_UPDATED_EVENT, onCartUpdated);
    return () => window.removeEventListener(CART_UPDATED_EVENT, onCartUpdated);
  }, []);

  // Staging state — what the user has clicked. Module-level so the
  // visual selection survives any re-mount. Initialised from module
  // state only (NOT from the cart, which is computed below the gate).
  const [stagingId, setStagingIdLocal] = useState<string | undefined>(paymentState.stagingId);
  const setStagingId = (id: string) => {
    paymentState.stagingId = id;
    setStagingIdLocal(id);
  };
  const [isSaving, setIsSavingLocal] = useState<boolean>(paymentState.isSaving);
  const setIsSaving = (v: boolean) => {
    paymentState.isSaving = v;
    setIsSavingLocal(v);
  };

  // === Conditional returns ONLY come after all hooks above. ===

  // Step 3 of the progressive step-by-step checkout: only render once
  // BOTH the address AND the shipping method are saved. Cumulatively
  // checking the address prevents the section from showing when the
  // cart has stale shipping methods (e.g. from a previous test) but
  // the address is empty. Cart from Puck context may be stale; fall
  // back to `checkoutSelections.{address,shippingId}` for instant
  // appearance after the user just saved those steps.
  if (!cart) return null;
  if (
    !stepStatus.isPaymentAccessible(
      cart,
      !!checkoutSelections.address,
      !!checkoutSelections.shippingId,
    )
  ) {
    return null;
  }
  // `trigger` is read here so React tracks it as a dep — without this
  // the unused read would be stripped and CART_UPDATED_EVENT would
  // not force a re-render.
  void trigger;

  // The cart's currently-active payment session (server truth)
  const serverSelectedId: string | undefined = cart?.payment_collection?.payment_sessions?.find(
    (s: any) => s.status === 'pending',
  )?.provider_id;
  // Effective staging = user's click (state) OR the server's session
  // (cart) as fallback.
  const effectiveStagingId = stagingId || serverSelectedId;

  if (!providers) {
    return <SharedRender {...props} methods={undefined} selectedId={effectiveStagingId} />;
  }

  const handleSelect = (id: string) => {
    if (id === effectiveStagingId || isSaving) return;
    setStagingId(id);
    // Persist the user choice locally so OrderReview shows the right
    // thing AND so the "Review Order" button can be enabled. The API
    // call is deferred to handleContinue — the user must explicitly
    // click Review Order to save the choice and advance.
    checkoutSelections.paymentId = id;
    const metaTitle = (id.replace(/^pp_/, '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()));
    checkoutSelections.paymentDescription = metaTitle || id;
  };

  // The "Review Order" button is what actually saves the payment
  // provider to Medusa. Radio clicks just stage the choice in
  // module-level state; this is the explicit "commit" step.
  const handleContinue = async () => {
    if (!stagingId || isSaving) return;
    setIsSaving(true);
    try {
      const res = await fetch('/api/cart/payment-method', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider_id: stagingId }),
      });
      if (!res.ok) {
        setStagingId(serverSelectedId);
      } else {
        // Mark this id as the last successfully saved one. We can't
        // rely on `cart.payment_collection` here because the Puck
        // context is SSR-frozen and won't update until the page
        // re-renders.
        paymentState.lastSavedId = stagingId;
        window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT));
      }
    } catch {
      setStagingId(serverSelectedId);
    } finally {
      setIsSaving(false);
    }
  };

  // Show the "Review Order" button whenever the user has a pending
  // staging that differs from the last successfully saved id. The
  // `lastSavedId` (from module state) is the source of truth here,
  // NOT the SSR-frozen `cart.payment_collection`.
  const effectiveLastSaved = paymentState.lastSavedId || serverSelectedId;
  const hasPendingChange = !!stagingId && stagingId !== effectiveLastSaved;
  const showContinueButton = hasPendingChange;

  return (
    <div>
      <SharedRender
        {...props}
        methods={providers.map((p: any) => ({ id: p.id }))}
        selectedId={effectiveStagingId}
        onSelect={handleSelect}
        onContinue={handleContinue}
        showContinueButton={showContinueButton}
        isLoading={isSaving}
        loadingText="Saving payment…"
      />
      {/* Same pattern as ShippingAddress: explicit "Saving…" line
          below the section during the API call. */}
      {isSaving && (
        <div className="mt-3 text-sm text-gray-500 px-1">Saving payment method…</div>
      )}
    </div>
  );
}

export const PaymentMethod: any = {
  ...Shared,
  render: (props: any) => <PaymentMethodImpl {...props} />,
};
export default PaymentMethod;
